import React, { useEffect, useMemo, useState } from "react";
import {
  startOfMonth, endOfMonth, startOfWeek, endOfWeek, addDays, format, isSameMonth, isSameDay,
} from "date-fns";
import { ChevronLeft, ChevronRight, Image as ImageIcon, List, Calendar as CalendarIcon } from "lucide-react";
import { apiClient, getApiErrorMessage } from "../../lib/apiClient";
import { Skeleton } from "../../components/ui/skeleton";
import { ErrorState } from "../../components/shared/ErrorState";
import { PodViewerDialog } from "../../components/deliveries/PodViewerDialog";
import { formatDate, todayLocal } from "../../lib/utils";
import { subscriptionCalendarDayMeta, deliveryStatusMeta } from "../../lib/statusMaps";

const WEEKDAY_KEYS = ["sunday", "monday", "tuesday", "wednesday", "thursday", "friday", "saturday"];

function LegendDot({ dotClass, label }) {
  return (
    <span className="flex items-center gap-1">
      <span className={`h-2 w-2 rounded-full ${dotClass}`} />
      {label}
    </span>
  );
}

export default function CustomerDeliveryHistory() {
  const [state, setState] = useState({ loading: true, error: null, items: [] });
  const [me, setMe] = useState(null);
  const [outletDays, setOutletDays] = useState(null);
  const [skippedDates, setSkippedDates] = useState([]);
  const [view, setView] = useState("list");
  const [month, setMonth] = useState(new Date());
  const [podFor, setPodFor] = useState(null);
  const today = todayLocal();

  const load = () => {
    setState((s) => ({ ...s, loading: true, error: null }));
    apiClient
      .get("/customer/deliveries/history", { params: { limit: 200 } })
      .then(({ data }) => setState({ loading: false, error: null, items: data.items }))
      .catch((error) => setState({ loading: false, error: getApiErrorMessage(error, "Could not load your history."), items: [] }));
    apiClient.get("/customer/me").then(({ data }) => setMe(data)).catch(() => {});
    apiClient.get("/outlet/deliver-days").then(({ data }) => setOutletDays(data)).catch(() => {});
    apiClient.get("/customer/deliveries/calendar").then(({ data }) => setSkippedDates(data.skipped_dates || [])).catch(() => {});
  };

  useEffect(() => { load(); }, []);

  const byDate = useMemo(() => {
    const m = new Map();
    state.items.forEach((d) => { if (d.date) m.set(d.date, d); });
    return m;
  }, [state.items]);

  const skippedSet = useMemo(() => new Set(skippedDates), [skippedDates]);

  const calendarDays = useMemo(() => {
    const start = startOfWeek(startOfMonth(month));
    const end = endOfWeek(endOfMonth(month));
    const days = [];
    let d = start;
    while (d <= end) { days.push(d); d = addDays(d, 1); }
    return days;
  }, [month]);

  const dayMeta = (dateStr, weekday, delivery) => subscriptionCalendarDayMeta({
    dateStr, weekday, delivery, todayStr: today,
    deliverDays: me?.deliver_days, outletDays, skippedDates: skippedSet,
    startDate: me?.start_date, endDate: me?.end_date,
  });

  // subscriptionCalendarDayMeta returns null for a real delivery that falls
  // outside the *current* subscription cycle's date range (e.g. a delivery
  // from before the latest renewal). It still has a real delivery_status
  // though, so color it using the exact same palette dayMeta already uses
  // for in-range days (bg-green-200/red-200/amber-200) — that mismatch
  // (a different, brand-token yellow for out-of-range "Pending"/"Delivered"
  // rows vs the raw amber/green used for in-range rows) was the reported
  // color inconsistency.
  const fallbackMeta = (delivery) => {
    const { label, variant } = deliveryStatusMeta(delivery.delivery_status);
    const classes = {
      success: { bg: "bg-green-200", text: "text-green-700" },
      warning: { bg: "bg-amber-200", text: "text-amber-800" },
      danger: { bg: "bg-red-200", text: "text-red-700" },
    }[variant] || { bg: "bg-slate-200", text: "text-slate-700" };
    return { label: delivery.status_label || label, ...classes };
  };

  return (
    <div data-testid="customer-history-page">
      <div className="mb-4 flex items-center justify-between">
        <h1 className="font-heading text-2xl font-bold tracking-tight text-text-main">My Tiffins</h1>
        <div className="flex rounded-xl border border-border bg-surface p-1">
          <button type="button" onClick={() => setView("list")} className={`rounded-lg p-2 transition-all active:scale-95 ${view === "list" ? "bg-primary text-white" : "text-text-muted"}`} data-testid="customer-history-view-list">
            <List size={16} strokeWidth={1.5} />
          </button>
          <button type="button" onClick={() => setView("calendar")} className={`rounded-lg p-2 transition-all active:scale-95 ${view === "calendar" ? "bg-primary text-white" : "text-text-muted"}`} data-testid="customer-history-view-calendar">
            <CalendarIcon size={16} strokeWidth={1.5} />
          </button>
        </div>
      </div>

      {state.loading ? (
        <Skeleton className="h-64 w-full rounded-2xl" />
      ) : state.error ? (
        <ErrorState message={state.error} onRetry={load} />
      ) : view === "list" ? (
        <div className="space-y-2.5" data-testid="customer-history-list">
          {state.items.length === 0 && (
            <div className="rounded-2xl border border-dashed border-border bg-surface py-14 text-center text-sm text-text-muted">No deliveries yet.</div>
          )}
          {state.items.map((d) => {
            const weekday = WEEKDAY_KEYS[new Date(`${d.date}T00:00:00`).getDay()];
            const meta = dayMeta(d.date, weekday, d) || fallbackMeta(d);
            return (
              <button
                key={d.id} type="button" onClick={() => (d.pof1 || d.pof2) && setPodFor(d)}
                className="flex w-full items-center justify-between rounded-2xl border border-border bg-surface p-4 text-left shadow-sm transition-all active:scale-[0.98]"
                data-testid={`customer-history-item-${d.id}`}
              >
                <div>
                  <p className="text-sm font-semibold text-text-main">{formatDate(d.date)}</p>
                  <p className="mt-0.5 text-xs text-text-muted">{d.arrival ? `Arrived ${d.arrival}` : "No arrival time recorded"}</p>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`rounded-full px-2.5 py-1 text-[11px] font-medium ${meta.bg} ${meta.text}`}>{meta.label}</span>
                  {(d.pof1 || d.pof2) && <ImageIcon size={16} strokeWidth={1.5} className="text-text-muted" />}
                </div>
              </button>
            );
          })}
        </div>
      ) : (
        <div className="rounded-2xl border border-border bg-surface p-4 shadow-sm" data-testid="customer-history-calendar">
          <div className="mb-3 flex items-center justify-between">
            <button type="button" onClick={() => setMonth((m) => addDays(startOfMonth(m), -1))} className="flex h-9 w-9 items-center justify-center rounded-full transition-all active:scale-90 hover:bg-background" data-testid="customer-history-calendar-prev">
              <ChevronLeft size={18} strokeWidth={1.5} />
            </button>
            <p className="text-sm font-semibold text-text-main">{format(month, "MMMM yyyy")}</p>
            <button type="button" onClick={() => setMonth((m) => addDays(endOfMonth(m), 1))} className="flex h-9 w-9 items-center justify-center rounded-full transition-all active:scale-90 hover:bg-background" data-testid="customer-history-calendar-next">
              <ChevronRight size={18} strokeWidth={1.5} />
            </button>
          </div>
          <div className="grid grid-cols-7 gap-1 text-center text-[10px] font-semibold uppercase text-text-muted">
            {["S", "M", "T", "W", "T", "F", "S"].map((d, i) => <div key={i}>{d}</div>)}
          </div>
          <div className="mt-1 grid grid-cols-7 gap-1">
            {calendarDays.map((d) => {
              const key = format(d, "yyyy-MM-dd");
              const delivery = byDate.get(key);
              const inMonth = isSameMonth(d, month);
              const isToday = isSameDay(d, new Date());
              const meta = dayMeta(key, WEEKDAY_KEYS[d.getDay()], delivery);
              return (
                <button
                  key={key} type="button" disabled={!meta}
                  onClick={() => delivery && (delivery.pof1 || delivery.pof2) && setPodFor(delivery)}
                  className={`aspect-square rounded-lg text-xs transition-all active:scale-90 ${!inMonth ? "text-text-muted/30" : "text-text-main"} ${
                    meta ? `${meta.bg} ${meta.text}` : ""
                  } ${isToday ? "ring-2 ring-primary" : ""}`}
                  data-testid={`customer-history-calendar-day-${key}`}
                >
                  {format(d, "d")}
                </button>
              );
            })}
          </div>
          <div className="mt-3 flex flex-wrap gap-x-3 gap-y-1 text-[10px] text-text-muted" data-testid="customer-history-calendar-legend">
            <LegendDot dotClass="bg-green-500" label="Delivered" />
            <LegendDot dotClass="bg-red-500" label="Not Delivered" />
            <LegendDot dotClass="bg-amber-500" label="Pending" />
            <LegendDot dotClass="bg-blue-500" label="Scheduled" />
            <LegendDot dotClass="bg-purple-500" label="Paused" />
            <LegendDot dotClass="bg-slate-500" label="Off" />
            <LegendDot dotClass="bg-stone-400" label="Unselected" />
          </div>
        </div>
      )}

      <PodViewerDialog open={!!podFor} onOpenChange={(v) => !v && setPodFor(null)} delivery={podFor} />
    </div>
  );
}
